/**
 * Dashboard Config Service
 * Service for managing shared dashboard configuration
 */

import { acquireTokenSilent } from '../config/msalConfig';

export interface SharedDashboardConfig {
  id: string | null;
  dashboard_config: DashboardConfig;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DashboardWidget {
  id: string;
  type: 'kpi' | 'bar' | 'donut' | 'area' | 'line' | 'table';
  title: string;
  config: any; // Widget-specific configuration
  position?: { x: number; y: number; w: number; h: number }; // For grid layout
}

export interface DashboardConfig {
  widgets: DashboardWidget[];
  layout?: 'grid' | 'list';
  timeWindow?: '7days' | '30days' | 'custom';
  filters?: any;
}

export class DashboardConfigService {
  private static baseUrl = (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';

  /**
   * Get Entra ID token from MSAL
   */
  private static async getToken(): Promise<string> {
    try {
      const tokenResult = await acquireTokenSilent();
      return tokenResult.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return '';
    }
  }

  /**
   * Get the shared dashboard configuration
   */
  static async getDashboardConfig(): Promise<SharedDashboardConfig> {
    try {
      const token = await this.getToken();
      const response = await fetch(`${this.baseUrl}/dashboard/config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching dashboard config:', error);
      throw new Error('Failed to fetch dashboard configuration');
    }
  }

  /**
   * Update the shared dashboard configuration
   */
  static async updateDashboardConfig(dashboardConfig: DashboardConfig): Promise<SharedDashboardConfig> {
    try {
      const token = await this.getToken();
      const response = await fetch(`${this.baseUrl}/dashboard/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          dashboard_config: dashboardConfig,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating dashboard config:', error);
      throw error;
    }
  }
}

